import { useCallback, useEffect, useState } from 'react';
import {
  CLOSED_ORDERS_PAGE_SIZE_DEFAULT,
  type ClosedOrderListItem,
  type SessionFilterOption,
} from 'shared-models/browser';
import { pastReceiptsService } from '../../../../services/past-receipts/past-receipts.service';
import type { PastReceiptsFilters } from './past-receipts.types';
import { buildClosedOrdersQuery } from './past-receipts.utils';

const PAGE_SIZE = CLOSED_ORDERS_PAGE_SIZE_DEFAULT;
const TEXT_DEBOUNCE_MS = 350;

const EMPTY_FILTERS: PastReceiptsFilters = {
  dateFrom: '',
  dateTo: '',
  search: '',
  customer: '',
  cardLast4: '',
  waiter: '',
  sessionId: null,
};

type Params = {
  opened: boolean;
  tenantId: string;
  employeeSession: string;
};

// Summary: Past-receipts list state — paginated lazy fetch + debounced text filters.
export function usePastReceiptsController({ opened, tenantId, employeeSession }: Params) {
  const [filters, setFilters] = useState<PastReceiptsFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<ClosedOrderListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionOptions, setSessionOptions] = useState<SessionFilterOption[]>([]);

  // Debounced scalars drive the fetch; raw text inputs stay snappy (Rules 21/22/28).
  const [debouncedText, setDebouncedText] = useState({ search: '', customer: '', cardLast4: '', waiter: '' });
  const { search: debouncedSearch, customer: debouncedCustomer, cardLast4: debouncedCardLast4, waiter: debouncedWaiter } = debouncedText;

  useEffect(() => {
    const next = {
      search: filters.search.trim(),
      customer: filters.customer.trim(),
      cardLast4: filters.cardLast4.length === 4 ? filters.cardLast4 : '',
      waiter: filters.waiter.trim(),
    };
    if (next.search === debouncedSearch && next.customer === debouncedCustomer
      && next.cardLast4 === debouncedCardLast4 && next.waiter === debouncedWaiter) return;
    const handle = window.setTimeout(() => {
      setDebouncedText(next);
      setPage(1);
    }, TEXT_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [filters.search, filters.customer, filters.cardLast4, filters.waiter,
    debouncedSearch, debouncedCustomer, debouncedCardLast4, debouncedWaiter]);

  useEffect(() => {
    if (!opened || !employeeSession) {
      return;
    }
    let active = true;
    setIsLoading(true);
    setError(null);

    const query = buildClosedOrdersQuery(page, PAGE_SIZE, {
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      search: debouncedSearch,
      customer: debouncedCustomer,
      cardLast4: debouncedCardLast4,
      waiter: debouncedWaiter,
      sessionId: filters.sessionId,
    });

    pastReceiptsService
      .listClosedOrders(tenantId, query, employeeSession)
      .then((result) => {
        if (!active) return;
        setItems(result.items);
        setTotal(result.total);
      })
      .catch((loadError: unknown) => {
        if (!active) return;
        setError(loadError instanceof Error ? loadError.message : 'טעינת הקבלות נכשלה');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    opened,
    tenantId,
    employeeSession,
    page,
    filters.dateFrom,
    filters.dateTo,
    filters.sessionId,
    debouncedSearch,
    debouncedCustomer,
    debouncedCardLast4,
    debouncedWaiter,
  ]);

  // Session options load once per open; failure is non-fatal (filter just stays empty).
  useEffect(() => {
    if (!opened || !employeeSession) {
      return;
    }
    let active = true;
    pastReceiptsService
      .listSessionFilterOptions(tenantId, employeeSession)
      .then((options) => {
        if (active) setSessionOptions(options);
      })
      .catch(() => {
        if (active) setSessionOptions([]);
      });
    return () => {
      active = false;
    };
  }, [opened, tenantId, employeeSession]);

  // Reset list state when the modal closes so reopening starts clean.
  useEffect(() => {
    if (!opened) {
      setFilters(EMPTY_FILTERS);
      setDebouncedText({ search: '', customer: '', cardLast4: '', waiter: '' });
      setPage(1);
      setItems([]);
      setTotal(0);
      setError(null);
      setIsLoading(false);
      setSessionOptions([]);
    }
  }, [opened]);

  const setFilterField = useCallback(
    <K extends keyof PastReceiptsFilters>(key: K, value: PastReceiptsFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      if (key === 'dateFrom' || key === 'dateTo' || key === 'sessionId') setPage(1);
    },
    [],
  );

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return {
    filters,
    setFilterField,
    page,
    setPage,
    pageCount,
    items,
    total,
    isLoading,
    error,
    sessionOptions,
    pageSize: PAGE_SIZE,
  };
}
